import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Typography, Box, RadioGroup, FormControlLabel, Radio, CircularProgress,
    Divider, Tabs, Tab, Alert
} from '@mui/material';
import { PayPalButtons, FUNDING, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import AirtelLogo from '../../../../../assets/Ourpartners/airtel.png';
import MtnLogo from '../../../../../assets/Ourpartners/mtn.png';
import baseURL from '../../../user/config';

const COLORS = {
    primary: '#2D4A3D',
    text: '#1a1a2e',
    textSecondary: '#6b7280',
    border: '#e5e7eb',
    backgroundLight: '#f9fafb',
};

// PayPal wrapper defined outside to prevent SDK reload
const PayPalButtonWrapper = ({ paypalClientId, paypalLoading, isValid, createOrder, onApprove }) => {
    if (paypalLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography>Loading PayPal...</Typography>
            </Box>
        );
    }
    if (!paypalClientId) {
        return (
            <Alert severity="error" sx={{ mt: 1 }}>
                PayPal is currently unavailable. Please try another payment method.
            </Alert>
        );
    }
    return (
        <PayPalScriptProvider options={{ clientId: paypalClientId, currency: 'USD', intent: 'capture' }}>
            <PayPalButtons
                fundingSource={FUNDING.PAYPAL}
                disabled={!isValid}
                style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' }}
                createOrder={createOrder}
                onApprove={onApprove}
                onError={(err) => {
                    console.error('PayPal error:', err);
                    toast.error('An error occurred with PayPal. Please try again.');
                }}
            />
        </PayPalScriptProvider>
    );
};

const fmtUGX = (ugx) => `UGX ${Math.round(ugx).toLocaleString()}`;

// Must match the server-side membershipFee() in membershipRoutes.js
const membershipFee = (amountUGX) => {
    if (amountUGX <= 25000)  return 1000;
    if (amountUGX <= 50000)  return 2000;
    if (amountUGX <= 75000)  return 3000;
    if (amountUGX <= 100000) return 4000;
    return 5000;
};

const MembershipPaymentDialog = ({
    open, onClose,
    formId, categoryId, categoryName,
    applicantName, applicantEmail,
    fieldData, voluntaryAmountUSD = 0, voluntaryAmountUGX = 0,
    totalAmountUSD, totalAmountUGX = 0,
    formTitle,
    formType = 'membership',
}) => {
    const isEvent = formType === 'event';
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0); // 0=Mobile Money, 1=Paystack, 2=PayPal
    const [loading, setLoading] = useState(false);

    // Mobile Money state
    const [paymentProvider, setPaymentProvider] = useState('MTN');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [pollStatus, setPollStatus] = useState(null); // null | 'polling' | 'Completed' | 'Failed'
    const pollRef = useRef(null);

    // Paystack state
    const [paystackEmail, setPaystackEmail] = useState('');

    // PayPal state
    const [paypalClientId, setPaypalClientId] = useState(null);
    const [paypalLoading, setPaypalLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setPaystackEmail(applicantEmail || '');
            setPollStatus(null);
            setPhoneNumber('');
        }
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [open, applicantEmail]);

    useEffect(() => {
        if (open && activeTab === 2 && !paypalClientId) {
            setPaypalLoading(true);
            fetch(`${baseURL}/paypal/config`)
                .then(r => r.json())
                .then(d => { if (d.paypalClientId) setPaypalClientId(d.paypalClientId); })
                .catch(() => {})
                .finally(() => setPaypalLoading(false));
        }
    }, [open, activeTab, paypalClientId]);

    const getPayload = () => ({
        formId,
        categoryId,
        applicantName,
        applicantEmail,
        fieldData,
        voluntaryAmountUGX,
    });

    const validatePhone = () => {
        let raw = phoneNumber.trim();
        if (raw.startsWith('0')) raw = '256' + raw.slice(1);
        if (!/^256\d{9}$/.test(raw)) {
            toast.error('Enter a valid phone number (e.g. 07XXXXXXXX).');
            return null;
        }
        const prefixes = {
            MTN: ['25676', '25677', '25678', '25679'],
            Airtel: ['25670', '25674', '25675'],
        };
        if (!prefixes[paymentProvider]?.some(p => raw.startsWith(p))) {
            toast.error(`The number doesn't match ${paymentProvider}.`);
            return null;
        }
        return raw;
    };

    // ── Mobile Money ────────────────────────────────────────────────────────

    const startPolling = (ref, applicationId) => {
        setPollStatus('polling');
        pollRef.current = setInterval(async () => {
            try {
                const res = await fetch(`${baseURL}/membership/status/${ref}`);
                const data = await res.json();
                if (data.status === 'Completed') {
                    clearInterval(pollRef.current);
                    setPollStatus('Completed');
                    setTimeout(() => navigate(`/membership-success?ref=${ref}`), 1200);
                } else if (data.status === 'Failed') {
                    clearInterval(pollRef.current);
                    setPollStatus('Failed');
                    toast.error('Payment failed. Please try again.');
                }
            } catch (_) {}
        }, 10000); // poll every 10 seconds
    };

    const handleMobileMoneySubmit = async () => {
        const phone = validatePhone();
        if (!phone) return;
        setLoading(true);
        try {
            const res = await fetch(`${baseURL}/membership/initiate/mobile-money`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...getPayload(), phoneNumber: phone, paymentProvider }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to initiate payment.');
            toast.info(data.message || 'Check your phone and enter your PIN to complete.');
            startPolling(data.externalReference, data.applicationId);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    // ── Paystack ────────────────────────────────────────────────────────────

    const handlePaystackSubmit = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(paystackEmail)) {
            toast.error('Enter a valid email address.');
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${baseURL}/membership/initiate/paystack`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...getPayload(), applicantEmail: paystackEmail }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to initiate payment.');
            window.location.href = data.authorization_url;
        } catch (err) {
            toast.error(err.message);
            setLoading(false);
        }
    };

    // ── PayPal ──────────────────────────────────────────────────────────────

    const createPayPalOrder = async () => {
        const res = await fetch(`${baseURL}/membership/initiate/paypal/order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(getPayload()),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to create PayPal order.');
        return data.orderID;
    };

    const onPayPalApprove = async (orderData) => {
        try {
            const res = await fetch(`${baseURL}/membership/initiate/paypal/capture/${orderData.orderID}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Payment capture failed.');
            if (data.status === 'COMPLETED') {
                toast.success('Payment successful!');
                navigate(`/membership-success?applicationId=${data.applicationId}`);
            }
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleClose = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        setPollStatus(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '1.05rem', color: COLORS.text, pb: 1 }}>
                {isEvent ? 'Complete Your Registration' : 'Complete Your Membership'}
            </DialogTitle>

            <DialogContent dividers>
                {/* Summary */}
                {(() => {
                    const fee = membershipFee(totalAmountUGX);
                    const chargedTotal = totalAmountUGX + fee;
                    return (
                        <Box sx={{ bgcolor: COLORS.backgroundLight, borderRadius: '10px', p: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                                    {categoryName} {isEvent ? 'ticket' : 'membership'}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtUGX(totalAmountUGX - voluntaryAmountUGX)}</Typography>
                            </Box>
                            {voluntaryAmountUGX > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>Additional contribution</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>+{fmtUGX(voluntaryAmountUGX)}</Typography>
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>Processing fee</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#b45309' }}>+{fmtUGX(fee)}</Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography sx={{ fontWeight: 700, color: COLORS.text }}>You will be charged</Typography>
                                <Typography sx={{ fontWeight: 800, color: COLORS.primary, fontSize: '1.05rem' }}>
                                    {fmtUGX(chargedTotal)}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })()}

                {/* Payment Tabs */}
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', color: COLORS.textSecondary },
                        '& .Mui-selected': { color: COLORS.primary },
                        '& .MuiTabs-indicator': { backgroundColor: COLORS.primary },
                    }}
                >
                    <Tab label="Mobile Money" />
                    <Tab label="Card (Paystack)" />
                    <Tab label="PayPal" />
                </Tabs>

                {/* Mobile Money Tab */}
                {activeTab === 0 && (
                    <Box>
                        {pollStatus === 'polling' ? (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <CircularProgress size={32} sx={{ color: COLORS.primary, mb: 2 }} />
                                <Typography sx={{ fontWeight: 600, color: COLORS.text, mb: 0.5 }}>
                                    Waiting for payment confirmation...
                                </Typography>
                                <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                                    Please check your phone and enter your Mobile Money PIN.
                                </Typography>
                            </Box>
                        ) : pollStatus === 'Completed' ? (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <Typography sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.1rem' }}>
                                    Payment confirmed! Redirecting...
                                </Typography>
                            </Box>
                        ) : pollStatus === 'Failed' ? (
                            <Box>
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    Payment failed. Please check your balance and try again.
                                </Alert>
                                <Button onClick={() => setPollStatus(null)} sx={{ textTransform: 'none', color: COLORS.primary }}>
                                    Try again
                                </Button>
                            </Box>
                        ) : (
                            <>
                                <Typography variant="body2" sx={{ color: COLORS.textSecondary, mb: 2 }}>
                                    Select your provider and enter your phone number to pay via Mobile Money.
                                </Typography>
                                <RadioGroup
                                    row value={paymentProvider}
                                    onChange={e => setPaymentProvider(e.target.value)}
                                    sx={{ mb: 2, gap: 2 }}
                                >
                                    {[
                                        { value: 'MTN', logo: MtnLogo },
                                        { value: 'Airtel', logo: AirtelLogo },
                                    ].map(p => (
                                        <FormControlLabel
                                            key={p.value}
                                            value={p.value}
                                            control={<Radio sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary } }} />}
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <img src={p.logo} alt={p.value} style={{ height: 20 }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.value}</Typography>
                                                </Box>
                                            }
                                            sx={{
                                                border: `2px solid ${paymentProvider === p.value ? COLORS.primary : COLORS.border}`,
                                                borderRadius: '8px', px: 1.5, py: 0.75, m: 0,
                                                bgcolor: paymentProvider === p.value ? `${COLORS.primary}08` : 'transparent',
                                            }}
                                        />
                                    ))}
                                </RadioGroup>
                                <TextField
                                    fullWidth label="Phone Number"
                                    placeholder="07XXXXXXXX or 2567XXXXXXXX"
                                    value={phoneNumber}
                                    onChange={e => setPhoneNumber(e.target.value)}
                                    size="small"
                                />
                                <Button
                                    fullWidth variant="contained"
                                    onClick={handleMobileMoneySubmit}
                                    disabled={loading || !phoneNumber}
                                    sx={{
                                        mt: 2, textTransform: 'none', fontWeight: 700,
                                        backgroundColor: COLORS.primary,
                                        '&:hover': { backgroundColor: '#1e3329' },
                                        borderRadius: '8px', py: 1.25,
                                    }}
                                >
                                    {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : `Pay ${fmtUGX(totalAmountUGX + membershipFee(totalAmountUGX))} via Mobile Money`}
                                </Button>
                            </>
                        )}
                    </Box>
                )}

                {/* Paystack Tab */}
                {activeTab === 1 && (
                    <Box>
                        <Typography variant="body2" sx={{ color: COLORS.textSecondary, mb: 2 }}>
                            Pay securely with your debit/credit card via Paystack.
                        </Typography>
                        <TextField
                            fullWidth label="Email for receipt"
                            value={paystackEmail}
                            onChange={e => setPaystackEmail(e.target.value)}
                            size="small" type="email" sx={{ mb: 2 }}
                        />
                        <Button
                            fullWidth variant="contained"
                            onClick={handlePaystackSubmit}
                            disabled={loading || !paystackEmail}
                            sx={{
                                textTransform: 'none', fontWeight: 700,
                                backgroundColor: COLORS.primary,
                                '&:hover': { backgroundColor: '#1e3329' },
                                borderRadius: '8px', py: 1.25,
                            }}
                        >
                            {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : `Pay ${fmtUGX(totalAmountUGX + membershipFee(totalAmountUGX))} with Paystack`}
                        </Button>
                    </Box>
                )}

                {/* PayPal Tab */}
                {activeTab === 2 && (
                    <Box>
                        <Typography variant="body2" sx={{ color: COLORS.textSecondary, mb: 2 }}>
                            Pay securely with your PayPal account.
                        </Typography>
                        <PayPalButtonWrapper
                            paypalClientId={paypalClientId}
                            paypalLoading={paypalLoading}
                            isValid={totalAmountUSD > 0}
                            createOrder={createPayPalOrder}
                            onApprove={onPayPalApprove}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} sx={{ textTransform: 'none', color: COLORS.textSecondary }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MembershipPaymentDialog;
